/* eslint-disable  @typescript-eslint/no-explicit-any */
import { RSocketClient, JsonSerializer, IdentitySerializer, ClientConfig as RSocketClientConfig } from 'rsocket-core';
import { ReactiveSocket } from 'rsocket-types';
import RSocketWebSocketClient from 'rsocket-websocket-client';

// FIXME: merge with SBApiWebsocketClient

const RECONNECT_INTERVAL_MS = 2000;
const MAX_RESUBSCRIBE_ATTEMPTS = 3;
const RESUBSCRIBE_INTERVAL_FACTOR_MS = 5000;
const MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT = 50;

type RSocketData = Record<string, never>;

type RSocketMetadata = string | Buffer | Uint8Array;

export type Subscription<T> = {
	ref: string;
	onSubscribed: () => void;
	onMessage: (message: T) => void;
	onError: (message: string, error: Error) => void;
	cancel?: () => void;
	request?: (n: number) => void;
	pendingRequestCount?: number;
};

// Could not find model for this error so stubbing it
type SubscriptionError = {
	source?: { explanation: string } | undefined;
};
const isStreamTerminatedAfterReceivingAnError = (error: Error | SubscriptionError): boolean =>
	'source' in error &&
	error.source !== undefined &&
	['APPLICATION_ERROR', 'REJECTED', 'CANCELED', 'INVALID'].includes(error.source.explanation);

const createRSocketClient = (
	url: string,
	wsCreator?: ((url: string) => WebSocket) | undefined
): RSocketClient<RSocketData, RSocketMetadata> => {
	const config: RSocketClientConfig<RSocketData, RSocketMetadata> = {
		setup: {
			dataMimeType: 'application/json',
			keepAlive: 60 * 1000,
			lifetime: 2 * 60 * 1000,
			metadataMimeType: 'message/x.rsocket.routing.v0'
		},
		transport: new RSocketWebSocketClient({
			url,
			debug: true,
			wsCreator
		}),
		serializers: {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			data: JsonSerializer,
			metadata: IdentitySerializer
		}
	};

	return new RSocketClient(config);
};

const establishRSocketConnection = (url: string, wsCreator?: ((url: string) => WebSocket) | undefined) =>
	new Promise<ReactiveSocket<RSocketData, RSocketMetadata>>((resolve, reject) => {
		const client = createRSocketClient(url, wsCreator);
		client.connect().subscribe({ onComplete: resolve, onError: reject });
	});

const getNextResubscribeCount = (currentRetryCount: number, hasReceivedFirstMessage: boolean): number => {
	if (hasReceivedFirstMessage) {
		// Start a new retry process
		// Start on 1 since 0 is initial call by consumer that we don't count as a retry
		return 1;
	}

	return currentRetryCount + 1;
};

const getMsUntilNextResubscribe = (nextRetryCount: number, hasReceivedFirstMessage: boolean): number => {
	if (hasReceivedFirstMessage) {
		return 0;
	}

	// Back off some so we don't spam sbbet
	return nextRetryCount * RESUBSCRIBE_INTERVAL_FACTOR_MS;
};

/**
 * Holds a websocket connection to sbbet
 * Keeps track of open subscriptions and ensures retries and refetching when reconnecting
 */
export default class RsocketClient {
	private baseUrl: string;

	private options: RsocketClientOptions;

	private connection: ReactiveSocket<RSocketData, RSocketMetadata> | null = null;

	// Array of all open subscriptions, will be used when subscribing and connecting
	private subscriptions = new Map<string, [Subscription<any>, string]>();

	// If consumer has called connect (but not disconnect), so we know if we should try and keep a connection open
	private shouldBeConnected = false;

	// When establishing the actual connection, so we don't accidentally opens two connections
	private isConnecting = false;

	// Id of the retry timeout, so we can cancel an upcoming retry if we get connected (or consumer calls disconnect)
	private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

	private bettingSessionExpiresOn: string | null;

	constructor(baseUrl: string, options?: RsocketClientOptions | undefined) {
		this.baseUrl = baseUrl;
		this.options = options || {};
		this.bettingSessionExpiresOn = null;
	}

	public getBaseUrl = () => this.baseUrl;

	public getOptions = () => this.options;

	public getSubscriptions = () => this.subscriptions;

	/**
	 * Connect to websocket, must be called before subscribing
	 */
	public connect = async () => {
		this.shouldBeConnected = true;
		await this.connectWithRetry();
	};

	/**
	 * Disconnect from websocket and unsubscribes to all current subscriptions
	 */
	public disconnect = () => {
		if (this.connection) {
			try {
				this.subscriptions.forEach((_, ref) => this.unsubscribe(ref));
				this.connection.close();
			} catch {
				// Do nothing
			}
		}

		if (this.reconnectTimeoutId) {
			clearTimeout(this.reconnectTimeoutId);
		}

		this.shouldBeConnected = false;
		this.connection = null;
		this.reconnectTimeoutId = null;
		this.subscriptions.clear();
	};

	public updateBettingSessionExpiration = (bettingSessionExpiresOn: string | null) => {
		this.bettingSessionExpiresOn = bettingSessionExpiresOn;
	};

	/**
	 * Start subscribing to provided subscription
	 * @param subscription Subscription to be subscribed to
	 */
	public subscribe = (route: string, subscription: Subscription<any>) => {
		if (this.subscriptions.has(subscription.ref)) {
			return;
		}

		this.subscriptions.set(subscription.ref, [subscription, route]);

		if (!this.connection) {
			// Consumer has not called connect() yet or we are currently trying to re/connect
			// The re/connect logic will re/subscribe once a connection has been established
			// So we don't need to do anything more here
			return;
		}

		this.subscribeWithRetry(route, subscription.ref);
	};

	/**
	 * Completely unsubscribes from a reference
	 * @param ref Subscription reference
	 */
	public unsubscribe = (ref: string) => {
		if (!this.subscriptions.has(ref)) {
			return;
		}

		const [subscription] = <[Subscription<any>, string]>this.subscriptions.get(ref);
		if (subscription?.cancel) {
			try {
				subscription.cancel();
			} catch {
				// Do nothing if we can't cancel
			}
		}

		this.subscriptions.delete(ref);
	};

	private connectWithRetry = async () => {
		if (!this.shouldBeConnected || this.isConnecting || this.connection) {
			return;
		}

		this.isConnecting = true;
		try {
			this.connection = await establishRSocketConnection(this.baseUrl, this.options.wsCreator);
		} catch (e) {
			// TODO pass this to consumer and handle it
		}
		this.isConnecting = false;

		if (!this.shouldBeConnected) {
			// Consumer called disconnect while connecting, lets keep it disconnected
			this.disconnect();
		} else if (this.connection) {
			this.handleNewConnection(this.connection, this.connectWithRetry);
		} else {
			this.reconnectTimeoutId = setTimeout(
				// eslint-disable-next-line @typescript-eslint/no-misused-promises
				this.connectWithRetry,
				RECONNECT_INTERVAL_MS
			);
		}
	};

	private handleNewConnection = (
		newConnection: ReactiveSocket<RSocketData, RSocketMetadata>,
		reconnect: () => Promise<void>
	) => {
		if (this.reconnectTimeoutId) {
			clearTimeout(this.reconnectTimeoutId);
			this.reconnectTimeoutId = null;
		}

		this.subscriptions.forEach(([, route], ref) => {
			this.subscribeWithRetry(route, ref);
		});

		newConnection.connectionStatus().subscribe(event => {
			if (event.kind === 'ERROR' || event.kind === 'CLOSED') {
				try {
					newConnection.close();
				} catch {
					// Do nothing
				}
				if (newConnection === this.connection) {
					this.connection = null;
					if (this.shouldBeConnected) {
						this.reconnectTimeoutId = setTimeout(
							// eslint-disable-next-line @typescript-eslint/no-misused-promises
							reconnect,
							RECONNECT_INTERVAL_MS
						);
					}
				}
			}
		});
	};

	private subscribeWithRetry = (route: string, ref: string, retryCount = 0) => {
		if (!this.connection || !this.subscriptions.has(ref)) {
			return;
		}

		const [{ onSubscribed, onMessage, onError }] = <[Subscription<any>, string]>this.subscriptions.get(ref);

		if (retryCount > MAX_RESUBSCRIBE_ATTEMPTS) {
			onError('Could not subscribe', new Error('Max retry count reached'));
			return;
		}

		if (this.bettingSessionExpiresOn != null && new Date(this.bettingSessionExpiresOn) < new Date()) {
			return;
		}

		let hasReceivedFirstMessage = false;

		this.connection
			.requestStream({
				data: {},
				metadata: String.fromCharCode(route.length) + route
			})
			.subscribe({
				onSubscribe: ({ cancel, request }) => {
					const [subscription] = <[Subscription<any>, string]>this.subscriptions.get(ref);
					this.subscriptions.set(ref, [
						{
							...subscription,
							pendingRequestCount: MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT,
							cancel,
							request
						},
						route
					]);

					request(MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT);

					onSubscribed();
				},
				onError: e => {
					if (isStreamTerminatedAfterReceivingAnError(e)) {
						// Then we want to resubscribe.
						const nextRetryCount = getNextResubscribeCount(retryCount, hasReceivedFirstMessage);
						const msUntilNextRetry = getMsUntilNextResubscribe(nextRetryCount, hasReceivedFirstMessage);
						setTimeout(() => this.subscribeWithRetry(route, ref, nextRetryCount), msUntilNextRetry);
					} else {
						onError('Could not subscribe: rsocket failure', e);
					}
				},
				onNext: msg => {
					// TODO Fix conversion
					// TODO Handle errors when parsing message (what if data is null etc)
					const response = <unknown>msg.data;

					hasReceivedFirstMessage = true;

					// TODO Fix conversion
					onMessage(response);

					const [subscription] = <[Subscription<any>, string]>this.subscriptions.get(ref);
					if (!subscription.pendingRequestCount || !subscription.request) {
						throw new Error('RSocket subscription missing properties');
					}

					// Make sure we let backend know we want more
					subscription.pendingRequestCount -= 1;
					if (subscription.pendingRequestCount === 0) {
						subscription.pendingRequestCount = MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT;
						subscription.request(MAX_PENDING_SUBSCRIPTION_REQUEST_COUNT);
					}
				}
			});
	};
}

// Singleton
let rsocketClient: RsocketClient | null = null;

export type RsocketClientOptions = {
	wsCreator?: ((url: string) => WebSocket) | undefined;
};

export const createRsocketClient = (websocketUrl: string, options?: RsocketClientOptions | undefined) => {
	rsocketClient = new RsocketClient(websocketUrl, options);
};

export const getRsocketClient = () => {
	if (rsocketClient == null) {
		throw new Error('Sportsbook betting rsocket client has not been configured');
	}
	return rsocketClient;
};

// HMR for Singleton
// add the old client when disposing the module
// and then create a new one with the same settings and subscriptions
if (import.meta && import.meta.webpackHot) {
	import.meta.webpackHot.dispose(data => {
		// eslint-disable-next-line
		data.rsocketClient = rsocketClient;
	});

	// eslint-disable-next-line
	if (import.meta.webpackHot.data?.rsocketClient) {
		const oldClient = <
			RsocketClient // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		>import.meta.webpackHot.data.rsocketClient;
		createRsocketClient(oldClient.getBaseUrl(), oldClient.getOptions());
		const oldSubscriptions = oldClient.getSubscriptions();

		oldSubscriptions.forEach(([subscription, route], ref) => {
			getRsocketClient().subscribe(route, {
				ref,
				onSubscribed: subscription.onSubscribed,
				onMessage: subscription.onMessage,
				onError: subscription.onError
			});
		});
		oldClient.disconnect();
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		getRsocketClient().connect();
	}
}
